import React, { FC, useMemo, useState } from "react";
import format from "date-fns/format";

import { Appointment } from "../AppointmentPreviewCard/types";

import StatusDialog from "../../Unknown/StatusDialog";
import Box from "../../Unknown/Box";
import CircularProgress from "../../Unknown/CircularProgress";

import useActionButtons from "./useActionButtons";
import useTranslations from "./translations";
import AppointmentModifyServicesForm from "./form";
import AppointmentSummaryDetails from "./summary";
import useCenterServiceOptions from "./useCenterServiceOptions";
import SelectAddOnsForm from "./addOnsForm";
import { useSubmitHandler } from "./useSubmitHandler";
import useStatAvailability from "./useStatAvailability";
import useInitialSelection from "./getInitialSelection";

export type AppointmentModifyServicesDialogProps = {
  appointment: Appointment;
  isOpened: boolean;
  onClose: () => void;
  handleSuccess: () => void | Promise<void>;
};

const AppointmentModifyServicesDialog: FC<
  AppointmentModifyServicesDialogProps
> = ({ appointment, isOpened, handleSuccess, onClose }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { translations } = useTranslations();

  const { onSubmit, isLoading } = useSubmitHandler({
    onSuccess: () => {
      onClose();
      handleSuccess();
    },
    onError: () => setErrorMessage(translations.errorMessage),
  });

  const onDialogClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const { initialServices, initialAddOnsIds, initialAddOnQuantities } =
    useInitialSelection(appointment);

  const optionsProps = useCenterServiceOptions({
    centerConfig: appointment.center,
    initialServices,
    initialAddOnsIds,
    initialAddOnQuantities,
    couponAmount: appointment.couponAmount,
    couponAddOnId: appointment.couponAddOnId,
  });

  const {
    isInitialized,
    summary,
    selectedCenterServices,
    selectedAddOns,
    isDirty,
    isAllCenterServicesValid,
  } = optionsProps;

  const apptDateStr = format(
    appointment.timeSlot.startedAtCenterTimezone,
    "yyyy-MM-dd",
  );

  const apptMinutes =
    appointment.timeSlot.startedAtCenterTimezone.getHours() * 60 +
    appointment.timeSlot.startedAtCenterTimezone.getMinutes();

  const requiredSpecialties = useMemo(() => {
    const specialties = new Set<string>();
    appointment.offerings.forEach((o) => {
      if (o.radiologySpecialty) specialties.add(o.radiologySpecialty);
    });
    appointment.packages.forEach((pkg) => {
      pkg.offerings.forEach((o) => {
        if (o.radiologySpecialty) specialties.add(o.radiologySpecialty);
      });
    });
    return Array.from(specialties);
  }, [appointment.offerings, appointment.packages]);

  const statDateRange = useMemo(
    () => ({ start: apptDateStr, end: apptDateStr }),
    [apptDateStr],
  );

  const statAvailability = useStatAvailability({
    centerId: appointment.center.id,
    requiredSpecialties,
    dateRange: statDateRange,
    skip: requiredSpecialties.length === 0,
  });

  const isStatAvailable = statAvailability.isAvailable(
    apptDateStr,
    apptMinutes,
  );

  const actionButtons = useActionButtons({
    handleClose: onDialogClose,
    handleSubmit: () =>
      onSubmit({
        appointmentId: appointment.id || "",
        selectedAddOns,
        selectedCenterServices,
      }),
    isLoading,
    errorMessage,
    isValid: isAllCenterServicesValid && isDirty,
  });

  return (
    <StatusDialog
      open={isOpened}
      title={translations.title}
      actionButtons={actionButtons}
      errorMessage={errorMessage}
      isLoading={isLoading}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxWidth: 800,
        },
      }}
    >
      {!appointment.center || !optionsProps.offerings || !isInitialized ? (
        <Box
          display="flex"
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          minHeight={150}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" gap={8}>
          <Box display="flex" flexDirection="column" gap={4}>
            <AppointmentModifyServicesForm
              optionsProps={optionsProps}
              isLoading={isLoading}
              centerConfig={appointment.center}
              isSubmitting={isLoading}
            />
            <SelectAddOnsForm
              centerConfig={appointment.center}
              addOns={optionsProps.addOns}
              isSubmitting={isLoading}
              selectedAddOns={optionsProps.selectedAddOns}
              selectedCenterServices={selectedCenterServices}
              onAddOnsChange={optionsProps.setSelectedAddOns}
              isStatAvailableForAppointmentTime={isStatAvailable}
            />
          </Box>
          <Box minWidth={280} maxWidth={280} flexShrink={0} top={0}>
            <AppointmentSummaryDetails
              centerConfig={appointment.center}
              selectedCenterServices={selectedCenterServices}
              selectedAddOns={optionsProps.selectedAddOns}
              discountSum={summary.discountSum}
              priceWithDiscount={summary.priceWithDiscount}
              centerAddOns={optionsProps.addOns || []}
            />
          </Box>
        </Box>
      )}
    </StatusDialog>
  );
};

export default AppointmentModifyServicesDialog;
